import { CopilotAssistant } from 'components/core/copilot'
import { useAlert } from 'hooks/useAlert'
import { SurfaceState } from 'lib/a2ui'
import { AgentConversationType, UIMessageActionItem } from 'pbts/c1/api/conversations/v1/conversations'
import { EditorValidationEnv } from 'pbts/c1/api/editor/v1/editor'
import { FC, ReactNode, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { CELChatZeroState } from './CELChatZeroState'
import { CELExpressionActionItem } from './CELExpressionActionItem'

type CELChatAssistantProps = {
    validationEnv: EditorValidationEnv
    initialExpression?: string
    onExpressionApply: (expression: string, explanation: string) => void
    onClose?: () => void
}

/**
 * Map EditorValidationEnv to the backend CEL env string.
 */
const editorEnvToCelEnv: Partial<Record<EditorValidationEnv, string>> = {
    [EditorValidationEnv.POLICY]: 'ENV_POLICY_CONDITIONAL',
    [EditorValidationEnv.POLICY_APPROVER]: 'ENV_POLICY_STEP',
    [EditorValidationEnv.DYNAMIC_GROUP_MEMBERSHIP]: 'ENV_DYNAMIC_GROUP_MEMBERSHIP',
    [EditorValidationEnv.WORKLOAD_FEDERATION]: 'ENV_WORKLOAD_FEDERATION',
}

const envToString = (env: EditorValidationEnv): string => {
    return editorEnvToCelEnv[env] ?? 'ENV_UNSPECIFIED'
}

/**
 * CELChatAssistant provides a mini chat interface for creating CEL expressions.
 */
export const CELChatAssistant: FC<CELChatAssistantProps> = ({
    validationEnv,
    initialExpression,
    onExpressionApply,
    onClose,
}) => {
    const toast = useAlert()
    const intl = useIntl()

    const metadata = useMemo(() => ({
        env: envToString(validationEnv),
        initial_expression: initialExpression ?? '',
    }), [validationEnv, initialExpression])

    const renderActionItem = useCallback((actionItem: UIMessageActionItem): ReactNode => {
        if (actionItem.actionItem.oneofKind === 'celExpressionActionItem') {
            const cel = actionItem.actionItem.celExpressionActionItem
            return (
                <CELExpressionActionItem
                    expression={cel.expression}
                    explanation={cel.explanation}
                    isValid={cel.isValid}
                    hideExplanation
                    onApply={(): void => onExpressionApply(cel.expression, cel.explanation)}
                />
            )
        }
        return null
    }, [onExpressionApply])

    /**
     * Handles A2UI surface actions locally. Returns true to skip backend submission.
     * Action names are pre-normalized by useChatAssistant (e.g., 'applycel' not 'apply_cel').
     */
    const handleLocalAction = useCallback((
        actionName: string,
        context: Record<string, string>,
        surfaceState: SurfaceState,
    ): boolean => {
        // actionName is already normalized by useChatAssistant
        if (actionName === 'applycel' || actionName === 'applyexpression') {
            const expression = surfaceState.dataModel.expression as string ?? context.expression
            const explanation = surfaceState.dataModel.explanation as string ?? context.explanation ?? ''
            if (expression) {
                onExpressionApply(expression, explanation)
                return true
            }
        }

        if (actionName === 'copycel' || actionName === 'copy') {
            const expression = surfaceState.dataModel.expression as string ?? context.expression
            if (expression) {
                navigator.clipboard.writeText(expression)
                toast(intl.formatMessage({
                    id: 'copilot_cel_copied',
                    defaultMessage: 'Expression copied to clipboard',
                }), 'success')
                return true
            }
        }

        return false
    }, [onExpressionApply, toast, intl])

    const zeroState = useMemo(
        () =>
            <CELChatZeroState initialExpression={initialExpression} />,
        [initialExpression],
    )

    return (
        <CopilotAssistant
            conversationType={AgentConversationType.CEL_HELPER}
            conversationTitle={intl.formatMessage({
                id: 'copilot_cel_conversation_title',
                defaultMessage: 'CEL expression',
            })}
            metadata={metadata}
            renderActionItem={renderActionItem}
            onLocalAction={handleLocalAction}
            zeroState={zeroState}
            placeholder={intl.formatMessage({
                id: 'copilot_cel_input_describe',
                defaultMessage: 'Describe the condition...',
            })}
            title={intl.formatMessage({
                id: 'copilot_cel_assistant_title',
                defaultMessage: 'CEL assistant',
            })}
            onClose={onClose}
        />
    )
}
